import { useMemo } from 'react';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { PresentationTemplateType } from '@/types/schema';
import { formatDateToMonDDYYYYWithTime } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';

import { useSystemPresentationTemplateWhere } from '../hooks/useSystemPresentationTemplateWhere';
import { sortSystemTemplates } from '../systemTemplateOrder.utils';
import { useSystemPresentationTemplateTableQuery } from './graphql/SystemPresentationTemplateTable.generated';

export interface SystemPresentationTemplateTableRowData {
  id: string;
  displayName: string;
  isPinned: boolean;
  type: PresentationTemplateType;
  createdBy: string;
  updatedAt: string;
  actions?: null;
}

interface UseSystemPresentationTemplateTableDataOutput
  extends ReturnType<typeof useSystemPresentationTemplateTableQuery> {
  rows: SystemPresentationTemplateTableRowData[];
}

export function useSystemPresentationTemplateTableData(): UseSystemPresentationTemplateTableDataOutput {
  const systemTemplateWhere = useSystemPresentationTemplateWhere();

  const response = useSystemPresentationTemplateTableQuery({
    variables: { systemTemplateWhere },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
  });

  const rows = useMemo<SystemPresentationTemplateTableRowData[]>(() => {
    return sortSystemTemplates(
      getNodes(response.data?.presentationTemplates)
    ).map<SystemPresentationTemplateTableRowData>((node) => ({
      id: node.id,
      displayName: node.displayName,
      isPinned: node.isPinned,
      type: node.type,
      createdBy: node.user?.displayName ?? EMPTY_CONTENT_HYPHEN,
      updatedAt: formatDateToMonDDYYYYWithTime(node.updatedAt),
    }));
  }, [response.data]);

  return {
    ...response,
    rows,
  };
}
